import { useCallback, useEffect, useMemo, useReducer } from "react";
import type { AnswersByKey, FunnelStep, QuestionStep } from "./types";

type FunnelState = {
  currentIndex: number;
  direction: 1 | -1;
  answers: AnswersByKey;
};

type FunnelAction =
  | { type: "next"; maxIndex: number }
  | { type: "back" }
  | { type: "goTo"; index: number; maxIndex: number; direction?: 1 | -1 }
  | { type: "setSingle"; key: string; value: string }
  | { type: "toggleMulti"; key: string; value: string }
  | { type: "setText"; key: string; value: string }
  | { type: "reset" };

type UseFunnelStateOptions = {
  flowKey: string;
  initialStepId?: string | null;
};

type PersistedFunnelState = {
  currentIndex?: number;
  stepId?: string;
  answers?: AnswersByKey;
};

const buildStorageKey = (flowKey: string, steps: FunnelStep[]): string => {
  const stepSignature = steps.map((step) => step.id).join("|");
  return `lumi:funnel:v2:${flowKey}:${stepSignature}`;
};

const INITIAL_STATE: FunnelState = {
  currentIndex: 0,
  direction: 1,
  answers: {}
};

const clampIndex = (value: number, maxIndex: number): number => {
  if (maxIndex < 0) {
    return 0;
  }
  return Math.min(Math.max(value, 0), maxIndex);
};

const resolveStepIndex = (steps: FunnelStep[], stepId: string | null | undefined): number => {
  if (!stepId) {
    return -1;
  }

  return steps.findIndex((step) => step.id === stepId);
};

const readPersistedState = (storageKey: string): PersistedFunnelState | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(storageKey);
    if (!rawValue) {
      return null;
    }

    const parsedValue: unknown = JSON.parse(rawValue);
    if (!parsedValue || typeof parsedValue !== "object") {
      return null;
    }

    return parsedValue as PersistedFunnelState;
  } catch {
    return null;
  }
};

const initializeState = (
  steps: FunnelStep[],
  maxIndex: number,
  storageKey: string,
  initialStepId?: string | null
): FunnelState => {
  const initialUrlIndex = resolveStepIndex(steps, initialStepId);
  if (initialUrlIndex >= 0) {
    return {
      ...INITIAL_STATE,
      currentIndex: clampIndex(initialUrlIndex, maxIndex)
    };
  }

  const persisted = readPersistedState(storageKey);
  if (!persisted) {
    return INITIAL_STATE;
  }

  const persistedStepIndex = resolveStepIndex(steps, persisted.stepId);
  const fallbackPersistedIndex =
    typeof persisted.currentIndex === "number" ? clampIndex(persisted.currentIndex, maxIndex) : 0;

  const restoredIndex = persistedStepIndex >= 0 ? persistedStepIndex : fallbackPersistedIndex;
  const restoredAnswers =
    persisted.answers && typeof persisted.answers === "object" ? persisted.answers : {};

  return {
    currentIndex: restoredIndex,
    direction: 1,
    answers: restoredAnswers
  };
};

const funnelReducer = (state: FunnelState, action: FunnelAction): FunnelState => {
  switch (action.type) {
    case "next":
      return {
        ...state,
        currentIndex: Math.min(state.currentIndex + 1, action.maxIndex),
        direction: 1
      };
    case "back":
      return {
        ...state,
        currentIndex: Math.max(0, state.currentIndex - 1),
        direction: -1
      };
    case "goTo": {
      const nextIndex = clampIndex(action.index, action.maxIndex);
      const direction = action.direction ?? (nextIndex >= state.currentIndex ? 1 : -1);
      return {
        ...state,
        currentIndex: nextIndex,
        direction
      };
    }
    case "setSingle":
      return {
        ...state,
        answers: {
          ...state.answers,
          [action.key]: action.value
        }
      };
    case "toggleMulti": {
      const existing = state.answers[action.key];
      const existingArray = Array.isArray(existing) ? existing : [];
      const hasValue = existingArray.includes(action.value);
      const nextArray = hasValue
        ? existingArray.filter((item) => item !== action.value)
        : [...existingArray, action.value];
      return {
        ...state,
        answers: {
          ...state.answers,
          [action.key]: nextArray
        }
      };
    }
    case "setText":
      return {
        ...state,
        answers: {
          ...state.answers,
          [action.key]: action.value
        }
      };
    case "reset":
      return INITIAL_STATE;
  }
};

const getAnswerKey = (step: FunnelStep): string => {
  if (step.kind === "question" && step.answerKey) {
    return step.answerKey;
  }
  if (step.kind === "email") {
    return step.answerKey;
  }
  if (step.kind === "payment") {
    return step.answerKey;
  }
  return step.id;
};

const isSingleQuestionComplete = (step: QuestionStep, value: unknown): boolean => {
  if (step.selection !== "single") {
    return false;
  }
  return typeof value === "string" && value.length > 0;
};

const isMultiQuestionComplete = (step: QuestionStep, value: unknown): boolean => {
  if (step.selection !== "multi") {
    return false;
  }
  if (!Array.isArray(value)) {
    return false;
  }
  const minimum = step.minSelections ?? 1;
  return value.length >= minimum;
};

const isEmailValid = (value: unknown): boolean => {
  if (typeof value !== "string") {
    return false;
  }
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
};

const isPaymentSelectionValid = (value: unknown): boolean => {
  return typeof value === "string" && value.trim().length > 0;
};

export const isStepComplete = (step: FunnelStep, answers: AnswersByKey): boolean => {
  const answerKey = getAnswerKey(step);
  const value = answers[answerKey];

  switch (step.kind) {
    case "welcome":
    case "affirmation":
    case "result":
      return true;
    case "question":
      return step.selection === "single"
        ? isSingleQuestionComplete(step, value)
        : isMultiQuestionComplete(step, value);
    case "loading": {
      const modalAnswer = answers[step.modalQuestion.answerKey];
      return typeof modalAnswer === "string" && modalAnswer.length > 0;
    }
    case "email":
      return isEmailValid(value);
    case "payment":
      return isPaymentSelectionValid(value);
  }
};

export const useFunnelState = (
  steps: FunnelStep[],
  options: UseFunnelStateOptions
) => {
  const storageKey = buildStorageKey(options.flowKey, steps);
  const maxIndex = steps.length - 1;
  const [state, dispatch] = useReducer(
    funnelReducer,
    undefined,
    () => initializeState(steps, maxIndex, storageKey, options.initialStepId)
  );
  const safeCurrentIndex = clampIndex(state.currentIndex, maxIndex);
  const currentStep = steps[safeCurrentIndex];

  useEffect(() => {
    if (state.currentIndex <= maxIndex) {
      return;
    }

    dispatch({ type: "goTo", index: maxIndex, maxIndex, direction: -1 });
  }, [maxIndex, state.currentIndex]);

  useEffect(() => {
    if (typeof window === "undefined" || !currentStep) {
      return;
    }

    try {
      window.localStorage.setItem(
        storageKey,
        JSON.stringify({
          currentIndex: safeCurrentIndex,
          stepId: currentStep.id,
          answers: state.answers
        } satisfies PersistedFunnelState)
      );
    } catch {
      // Ignore quota or storage availability errors.
    }
  }, [currentStep, safeCurrentIndex, state.answers, storageKey]);

  const canContinue = useMemo(() => {
    if (!currentStep) {
      return false;
    }
    return isStepComplete(currentStep, state.answers);
  }, [currentStep, state.answers]);

  const goToIndex = useCallback(
    (index: number) => {
      dispatch({ type: "goTo", index, maxIndex });
    },
    [maxIndex]
  );

  const goNext = useCallback(() => {
    dispatch({ type: "next", maxIndex });
  }, [maxIndex]);

  const goBack = useCallback(() => {
    dispatch({ type: "back" });
  }, []);

  return {
    answers: state.answers,
    currentIndex: safeCurrentIndex,
    currentStep,
    direction: state.direction,
    canGoBack: safeCurrentIndex > 0,
    isLastStep: safeCurrentIndex >= maxIndex,
    canContinue,
    getAnswerKey,
    setSingleAnswer: (key: string, value: string) => {
      dispatch({ type: "setSingle", key, value });
    },
    toggleMultiAnswer: (key: string, value: string) => {
      dispatch({ type: "toggleMulti", key, value });
    },
    setTextAnswer: (key: string, value: string) => {
      dispatch({ type: "setText", key, value });
    },
    goToIndex,
    goNext,
    goBack,
    resetState: () => {
      dispatch({ type: "reset" });
    }
  };
};
